import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  levels,
  totalItemsCount,
  XP_PER_ITEM,
  XP_LEVEL_BONUS,
} from '../data/roadmapData';
import { isItemComplete } from '../data/roadmapValidation';

const STORAGE_KEY = 'roadmap-freelance-v1';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

const daysBetween = (a, b) => {
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  return Math.round((db - da) / 86400000);
};

// Construit l'état initial : pré-remplit chaque champ avec sa valeur `prefill`.
const buildInitialAnswers = () => {
  const answers = {};
  levels.forEach((level) => {
    level.items.forEach((item) => {
      answers[item.id] = {};
      item.fields.forEach((f) => {
        answers[item.id][f.key] = f.prefill != null ? f.prefill : '';
      });
    });
  });
  return answers;
};

const loadState = () => {
  const base = {
    answers: buildInitialAnswers(),
    validated: {},
    streak: { count: 0, lastDate: null },
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw);
    // Fusionne : on garde les pré-remplissages pour les champs jamais touchés.
    const answers = base.answers;
    if (saved.answers) {
      Object.keys(saved.answers).forEach((itemId) => {
        answers[itemId] = { ...(answers[itemId] || {}), ...saved.answers[itemId] };
      });
    }
    return {
      answers,
      validated: saved.validated || {},
      streak: saved.streak || base.streak,
    };
  } catch (e) {
    return base;
  }
};

export default function useRoadmapProgress() {
  const [state, setState] = useState(loadState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* quota / private mode — on ignore */
    }
  }, [state]);

  const setFieldAnswer = useCallback((itemId, fieldKey, value) => {
    setState((s) => ({
      ...s,
      answers: {
        ...s.answers,
        [itemId]: { ...(s.answers[itemId] || {}), [fieldKey]: value },
      },
    }));
  }, []);

  const validateItem = useCallback((itemId) => {
    setState((s) => {
      if (s.validated[itemId]) return s; // déjà validé, pas de double comptage streak
      const today = todayStr();
      let { count, lastDate } = s.streak;
      if (lastDate === today) {
        // déjà actif aujourd'hui
      } else if (lastDate && daysBetween(lastDate, today) === 1) {
        count += 1;
      } else {
        count = 1;
      }
      return {
        ...s,
        validated: { ...s.validated, [itemId]: true },
        streak: { count, lastDate: today },
      };
    });
  }, []);

  const resetAll = useCallback(() => {
    setState({
      answers: buildInitialAnswers(),
      validated: {},
      streak: { count: 0, lastDate: null },
    });
  }, []);

  const isItemValidated = useCallback(
    (itemId) => !!state.validated[itemId],
    [state.validated]
  );

  const levelProgress = useCallback(
    (levelId) => {
      const level = levels.find((l) => l.id === levelId);
      const total = level.items.length;
      const done = level.items.filter((it) => state.validated[it.id]).length;
      return { done, total, ratio: total ? done / total : 0 };
    },
    [state.validated]
  );

  const isLevelComplete = useCallback(
    (levelId) => {
      const p = levelProgress(levelId);
      return p.done === p.total;
    },
    [levelProgress]
  );

  const isLevelUnlocked = useCallback(
    (levelId) => {
      if (levelId === 1) return true;
      return isLevelComplete(levelId - 1);
    },
    [isLevelComplete]
  );

  const globalProgress = useMemo(() => {
    const done = Object.values(state.validated).filter(Boolean).length;
    return { done, total: totalItemsCount, ratio: done / totalItemsCount };
  }, [state.validated]);

  const xp = useMemo(() => {
    const itemsXp = globalProgress.done * XP_PER_ITEM;
    const levelBonus =
      levels.filter((l) => levelProgress(l.id).done === l.items.length).length *
      XP_LEVEL_BONUS;
    return itemsXp + levelBonus;
  }, [globalProgress.done, levelProgress]);

  const earnedBadges = useMemo(
    () =>
      levels
        .filter((l) => levelProgress(l.id).done === l.items.length)
        .map((l) => l.badge),
    [levelProgress]
  );

  // Premier item non validé d'un niveau (pour reprise / "Continuer").
  const firstPendingItem = useCallback(
    (levelId) => {
      const level = levels.find((l) => l.id === levelId);
      return level.items.find((it) => !state.validated[it.id]) || null;
    },
    [state.validated]
  );

  // Niveau + item à reprendre globalement (dernier point non terminé).
  const resumeTarget = useMemo(() => {
    for (const level of levels) {
      if (level.id !== 1 && !isLevelComplete(level.id - 1)) break;
      const item = level.items.find((it) => !state.validated[it.id]);
      if (item) return { levelId: level.id, itemId: item.id };
    }
    return null;
  }, [state.validated, isLevelComplete]);

  // Aide à savoir si la complétude "validée" correspond bien aux réponses.
  const itemMeetsRules = useCallback(
    (item) => isItemComplete(item, state.answers),
    [state.answers]
  );

  return {
    answers: state.answers,
    validated: state.validated,
    streak: state.streak,
    setFieldAnswer,
    validateItem,
    resetAll,
    isItemValidated,
    levelProgress,
    isLevelComplete,
    isLevelUnlocked,
    globalProgress,
    xp,
    earnedBadges,
    firstPendingItem,
    resumeTarget,
    itemMeetsRules,
  };
}
